import React from 'react';
import styled from 'styled-components';
import * as Icon from 'react-feather';
import { useHistory } from 'react-router-dom';
import routes from 'routes';

// Container matches the product page (full-width container-fluid style)
const PageWrapper = styled.div`
  width: 100%;
  max-width: 100%;
`;

const Inner = styled.div`
  padding: 0 24px 24px 24px; /* match page padding */
  width: 100%;
  max-width: 100%;
  margin: 0; /* span full width */
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 16px 0 20px 0;
`;

const Title = styled.h1`
  font-size: 1.25rem;
  margin: 0;
  color: #333;
  font-weight: 600;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 22px;
  flex-wrap: wrap;
  width: 100%;
`;

const StatCard = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 18px 20px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.06);
  flex: 1 1 220px; /* grow to fill available width equally, but keep a sensible min */
  min-width: 220px;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: transform 120ms ease, box-shadow 120ms ease;
  &:hover { transform: translateY(-4px); box-shadow: 0 6px 18px rgba(0,0,0,0.08); }
`;

const StatNumber = styled.div`
  font-weight: 800;
  color: #001f5f;
  font-size: 24px;
`;

const StatLabel = styled.div`
  color: #666;
  font-size: 14px;
`;

const Section = styled.section`
  margin-bottom: 26px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 16px;
  color: #333;
`;

// Improved grid organization - responses like product page, but card-focused
const Grid = styled.div`
  display: grid;
  /* allow more columns on large screens so cards span the full width */
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 18px;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 14px;
  border-radius: 8px;
  background: #fafafa;
  border: 1px solid #eee;
  min-height: 120px;
`;

const CardTop = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const CardIcon = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-radius: 8px;
  border: 1px solid rgba(0,31,95,0.08);
  color: #001f5f; /* dark blue accent */
  flex-shrink: 0;
`;

const CardTitle = styled.div`
  font-weight: 700;
  color: #222;
  font-size: 15px;
`;

const CardSubtitle = styled.div`
  font-size: 13px;
  color: #666;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 12px;
  margin-left: calc(48px + 12px);

  @media (max-width: 360px) {
    margin-left: 0;
  }
`;

const ConfigureButton = styled.button`
  padding: 8px 12px;
  background: #001f5f;
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  &:hover { filter: brightness(0.95); }
`;

export default function IntegrationPanel() {
  const history = useHistory();

  // Data model for the grid - mirrors sections in the app
  const sections = [
    {
      key: 'certificate',
      title: 'Certificate Issuance',
      items: [
        { id: 'dmvic', title: 'DMVIC', subtitle: 'DMVIC certificate issuance integration', icon: <Icon.Truck /> },
        { id: 'directline', title: 'DirectLine', subtitle: 'DirectLine certificate issuance', icon: <Icon.Link /> },
      ],
    },
    {
      key: 'payment',
      title: 'Payment',
      items: [
        { id: 'mpesa', title: 'Safaricom Mpesa', subtitle: 'Mobile payments via M-Pesa', icon: <Icon.CreditCard /> },
      ],
    },
    {
      key: 'valuers',
      title: 'Valuers',
      items: [
        { id: 'links', title: 'Links', subtitle: 'Manage valuer links and callbacks', icon: <Icon.Link /> },
      ],
    },
  ];

  const totalIntegrations = sections.reduce((sum, s) => sum + s.items.length, 0);

  function handleConfigure(id) {
    // navigate to specific integration pages
    if (id === 'dmvic') {
      history.push(routes.integrationDMVIC);
      return;
    }
    // placeholder behavior for others
    // eslint-disable-next-line no-alert
    alert(`Configure ${id}`);
  }

  return (
    <PageWrapper>
      <div className="container-fluid px-0" style={{ width: '100%', maxWidth: '100%' }}>
        <Inner>
          <Header>
            <Title>List of integrations</Title>
          </Header>

          <StatsRow>
            <StatCard>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: '#f1f6ff', display: 'grid', placeItems: 'center', color: '#001f5f' }}>
                  <Icon.Link />
                </div>
                <div>
                  <StatNumber>{totalIntegrations}</StatNumber>
                  <StatLabel>Total Integrations</StatLabel>
                </div>
              </div>
            </StatCard>

            {sections.map((s) => (
              <StatCard key={s.key}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: '#fff7ed', display: 'grid', placeItems: 'center', color: '#ff8a00' }}>
                    <Icon.Box />
                  </div>
                  <div>
                    <StatNumber>{s.items.length}</StatNumber>
                    <StatLabel>{s.title}</StatLabel>
                  </div>
                </div>
              </StatCard>
            ))}
          </StatsRow>

          {/* Sections */}
          {sections.map((section) => (
            <Section key={section.key}>
              <SectionTitle>{section.title}</SectionTitle>
              <Grid>
                {section.items.map((item) => (
                  <Card key={item.id}>
                    <div>
                      <CardTop>
                        <CardIcon>{item.icon}</CardIcon>
                        <div>
                          <CardTitle>{item.title}</CardTitle>
                          <CardSubtitle>{item.subtitle}</CardSubtitle>
                        </div>
                      </CardTop>
                    </div>

                    <CardFooter>
                      <ConfigureButton onClick={() => handleConfigure(item.id)}>Configure</ConfigureButton>
                    </CardFooter>
                  </Card>
                ))}
              </Grid>
            </Section>
          ))}
        </Inner>
      </div>
    </PageWrapper>
  );
}
